import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { z } from 'zod';
import InputField from '../commonComponent/InputField';
// import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import axios from 'axios';
import Cookies from 'js-cookie';
import { ToastContainer, toast } from 'react-toastify';

const AQ_BASE_URL = process.env.REACT_APP_AQ_BASE_URL;

// const GOOGLE_CLIENT_ID =
//   "379455138009-gg187pl9mma89mq6417fkij2u5egiu70.apps.googleusercontent.com";

const registrationSchema = z
  .object({
    first_name: z
      .string()
      .nonempty('First name is required.')
      .max(10, 'Cannot exceed 10 characters.'),
    last_name: z
      .string()
      .nonempty('Last name is required.')
      .max(10, 'Last name cannot exceed 10 characters.'),
    username: z.string().nonempty('Username is required.'),
    email: z.string().email('Email is not valid.'),
    password: z
      .string()
      .nonempty('Password is required.')
      .min(8, 'At least 8 characters are required.')
      .max(16, 'The password must be a maximum of 16 characters.')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter.')
      .regex(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character.')
      .regex(/\d/, 'Password must contain at least one number.'),
    confirmpassword: z.string().min(8, 'At least 8 characters are required.'),
    countryCode: z.string().nonempty('Country code is required.'),
    phone: z
      .string()
      .nonempty('Mobile number is required.')
      .length(10, 'Phone number must be exactly 10 digits.')
      .regex(/^\d+$/, 'Phone number must be numeric.'),
    termsAccepted: z.boolean().refine((val) => val === true, {
      message: 'You must accept the terms and conditions.',
    }),
  })
  .refine((data) => data.password === data.confirmpassword, {
    message: 'Confirm Password should match the password.',
    path: ['confirmpassword'],
  });

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    username: '',
    email: '',
    password: '',
    confirmpassword: '',
    countryCode: '+91',
    phone: '',
    company_name: '',
    termsAccepted: false,
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'phone') {
      if (!/^\d*$/.test(value)) return;
    }
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Validate form data
      registrationSchema.parse(formData);

      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        username: formData.username,
        email: formData.email,
        password: formData.password,
        mobile_number: Number(formData.phone),
        country_code: formData.countryCode,
        company_name: 'Rgs-tech',
        first_login: true, // Ensure the first_login is included
        role: ['Super_Admin'], // Add the role field here
      };
      console.log('Payload to be sent:', payload); // Log the payload
      const response = await fetch(`${AQ_BASE_URL}/aq/api/register_for_admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Registration successful');
        localStorage.setItem('email', formData.email); // Store the email
        window.location.href = data.redirect_url; // ✅ Redirect to /admin-otp-verification
      } else {
        toast.error(data.message || 'Registration failed');
      }
    } catch (err) {
      if (err.errors) {
        const formattedErrors = {};
        err.errors.forEach(({ path, message }) => {
          formattedErrors[path[0]] = message;
        });
        setErrors(formattedErrors);
      } else {
        toast.error('An unexpected error occurred. Please try again.');
        console.error('Registration error:', err);
      }
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setGoogleLoading(true);

      const response = await axios.post(`${AQ_BASE_URL}/aq/api/auth/google/callback`, {
        id_token: credentialResponse.credential,
      });

      const data = response.data;

      // Store tokens in cookies
      Cookies.set('access_token', data.access_token, {
        expires: 1, // expires in 1 day
        secure: true, // only works on HTTPS
        sameSite: 'Strict', // prevents CSRF
        path: '/',
      });

      Cookies.set('refresh_token', data.refresh_token, {
        expires: 7, // expires in 7 days
        secure: true,
        sameSite: 'Strict',
        path: '/',
      });

      // Redirect to the dashboard
      window.location.href = data.redirect_url;
    } catch (error) {
      console.error('Google OAuth failed:', error?.response?.data || error.message);
      alert('Google login failed. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleError = () => {
    console.error('Google OAuth failed');
    alert('Google login failed. Please try again.');
  };

  return (
    <div className="relative min-h-screen bg-gray-100">
      {/* Top Left Logo */}
      <div className="fixed top-0 left-0 p-6 z-10">
        <div className="flex items-center space-x-2">
          <div className="w-10 h-11 bg-black rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-xl">EQ</span>
          </div>
          <div>
            <span className="text-2xl font-bold text-gray-900 tracking-tight block">
              EnterpriseQuest
            </span>
            <span className="text-xs text-gray-800 tracking-tight block">
              Powered by RhombusQuest
            </span>
          </div>
        </div>
      </div>

      {/* Registration Form Container */}
      <div className="flex items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
        <div className="w-full max-w-md bg-white border border-gray-300 rounded-lg shadow-lg p-6 sm:p-8 mx-2 sm:mx-0">
          <h1 className="mt-1 text-2xl font-bold text-black text-center">Register</h1>
          <h1 className="text-sm text-gray-500 text-center">
            Enter your credentials to register your account.
          </h1>
          <form onSubmit={handleSubmit} className="space-y-2 mt-6">
            <div className="flex flex-col sm:flex-row sm:gap-3 w-full">
              <InputField
                id="first_name"
                name="first_name"
                label="First Name"
                type="text"
                placeholder="Enter your first name."
                value={formData.first_name}
                onChange={handleChange}
                error={errors.first_name}
                className="flex-1"
              />
              <InputField
                id="last_name"
                name="last_name"
                label="Last Name"
                type="text"
                placeholder="Enter your last name."
                value={formData.last_name}
                onChange={handleChange}
                error={errors.last_name}
                className="flex-1"
              />
            </div>

            <div className="flex flex-col sm:flex-row sm:gap-3 w-full justify-between">
              <InputField
                id="username"
                name="username"
                label="Username"
                type="text"
                placeholder="Enter your username."
                value={formData.username}
                onChange={handleChange}
                error={errors.username}
                className="flex-1 "
              />
              <InputField
                id="email"
                name="email"
                label="Email"
                type="email"
                placeholder="Enter your email."
                value={formData.email}
                onChange={handleChange}
                error={errors.email}
                className="flex-1"
              />
            </div>
            <InputField
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              label="Password"
              placeholder="Enter your Password."
              value={formData.password}
              onChange={handleChange}
              error={errors.password}
              showPasswordToggle
              onToggleShowPassword={() => setShowPassword(!showPassword)}
            />
            <InputField
              id="confirmpassword"
              name="confirmpassword"
              type={showConfirmPassword ? 'text' : 'password'}
              label="Confirm Password"
              placeholder="Confirm your password."
              value={formData.confirmpassword}
              onChange={handleChange}
              error={errors.confirmpassword}
              showPasswordToggle
              onToggleShowPassword={() => setShowConfirmPassword(!showConfirmPassword)}
            />
            <div className="flex flex-col w-full">
              <label htmlFor="phone" className="block text-sm font-medium text-gray-950">
                Mobile No.
              </label>
              <div className="flex items-center gap-1 mt-1">
                <div className="border border-gray-300 rounded-md">
                  <select
                    id="countryCode"
                    name="countryCode"
                    value={formData.countryCode}
                    onChange={handleChange}
                    className="block w-20 border-none rounded-md py-2 px-2.5 text-gray-900 text-xs"
                  >
                    <option value="+91">+91 (India)</option>
                    <option value="+1">+1 (US)</option>
                  </select>
                </div>
                <div className="flex-1">
                  <InputField
                    id="phone"
                    name="phone"
                    type="text"
                    placeholder="Enter your mobile number."
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full border-gray-300 rounded-md px-2 py-1"
                  />
                </div>
              </div>
              {errors.phone && <span className="text-red-700 ml-20 text-xs">{errors.phone}</span>}
            </div>

            <div className="flex flex-col items-center justify-center mt-4">
              <div className="flex items-center">
                <input
                  id="termsAccepted"
                  name="termsAccepted"
                  type="checkbox"
                  checked={formData.termsAccepted}
                  onChange={handleChange}
                  className="h-4 w-4 text-green-600 border-gray-300 rounded"
                />
                <label htmlFor="termsAccepted" className="ml-2 text-sm text-gray-900">
                  I accept the terms and conditions
                </label>
              </div>
              {errors.termsAccepted && (
                <span className="text-red-700 pl-1 text-xs">{errors.termsAccepted}</span>
              )}
            </div>

            <button
              type="submit"
              className="block w-full bg-black hover:bg-gray-800 text-white font-bold py-2 px-4 rounded-md"
            >
              Register
            </button>

            <div className="mt-4 text-center">
              <span className="text-sm">
                Already have an account?{' '}
                <Link to="/login" className="text-gray-950 hover:underline">
                  Login
                </Link>
              </span>
            </div>
          </form>
        </div>
      </div>
      <ToastContainer position="top-center" autoClose={3000} hideProgressBar />
    </div>
  );
};

export default Register;

// import React, { useState } from "react";
// import { useNavigate, Link } from "react-router-dom";
// import { z } from "zod";
// import InputField from "../commonComponent/InputField";
// // import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
// import axios from "axios";
// import Cookies from "js-cookie";
// import { ToastContainer, toast } from "react-toastify";

// const AQ_BASE_URL = process.env.REACT_APP_AQ_BASE_URL;

// // const GOOGLE_CLIENT_ID =
// //   "379455138009-gg187pl9mma89mq6417fkij2u5egiu70.apps.googleusercontent.com";

// const registrationSchema = z
//   .object({
//     first_name: z
//       .string()
//       .nonempty("First name is required.")
//       .max(10, "Cannot exceed 10 characters."),
//     last_name: z
//       .string()
//       .nonempty("Last name is required.")
//       .max(10, "Last name cannot exceed 10 characters."),
//     username: z.string().nonempty("Username is required."),
//     email: z.string().email("Email is not valid."),
//     password: z
//       .string()
//       .nonempty("Password is required.")
//       .min(8, "At least 8 characters are required.")
//       .max(16, "The password must be a maximum of 16 characters.")
//       .regex(/[A-Z]/, "Password must contain at least one uppercase letter.")
//       .regex(
//         /[!@#$%^&*(),.?":{}|<>]/,
//         "Password must contain at least one special character."
//       )
//       .regex(/\d/, "Password must contain at least one number."),
//     confirmpassword: z.string().min(8, "At least 8 characters are required."),
//     countryCode: z.string().nonempty("Country code is required."),
//     phone: z
//       .string()
//       .nonempty("Mobile number is required.")
//       .length(10, "Phone number must be exactly 10 digits.")
//       .regex(/^\d+$/, "Phone number must be numeric."),
//     termsAccepted: z.boolean().refine((val) => val === true, {
//       message: "You must accept the terms and conditions.",
//     }),
//   })
//   .refine((data) => data.password === data.confirmpassword, {
//     message: "Confirm Password should match the password.",
//     path: ["confirmpassword"],
//   });

// const Register = () => {
//   const navigate = useNavigate();
//   const [formData, setFormData] = useState({
//     first_name: "",
//     last_name: "",
//     username: "",
//     email: "",
//     password: "",
//     confirmpassword: "",
//     countryCode: "+91",
//     phone: "",
//     company_name: "",
//     termsAccepted: false,
//   });
//   const [errors, setErrors] = useState({});
//   const [showPassword, setShowPassword] = useState(false);
//   const [showConfirmPassword, setShowConfirmPassword] = useState(false);
//   const [googleLoading, setGoogleLoading] = useState(false);

//   const handleChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     if (name === "phone") {
//       if (!/^\d*$/.test(value)) return;
//     }
//     setFormData({
//       ...formData,
//       [name]: type === "checkbox" ? checked : value,
//     });
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     try {
//       // Validate form data
//       registrationSchema.parse(formData);

//       const payload = {
//         first_name: formData.first_name,
//         last_name: formData.last_name,
//         username: formData.username,
//         email: formData.email,
//         password: formData.password,
//         mobile_number: Number(formData.phone),
//         country_code: formData.countryCode,
//         company_name: "Rgs-tech",
//         first_login: true, // Ensure the first_login is included
//         role: ["Super_Admin"], // Add the role field here
//       };
//       console.log("Payload to be sent:", payload);  // Log the payload
//       const response = await fetch(`${AQ_BASE_URL}/aq/api/register_for_admin`, {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//         },
//         body: JSON.stringify(payload),
//       });

//       const data = await response.json();

//       if (response.ok && data.success) {
//         toast.success("Registration successful");
//         localStorage.setItem("email", formData.email); // Store the email
//         window.location.href = data.redirect_url; // ✅ Redirect to /admin-otp-verification
//       } else {
//         toast.error(data.message || "Registration failed");
//       }
//     } catch (err) {
//       if (err.errors) {
//         const formattedErrors = {};
//         err.errors.forEach(({ path, message }) => {
//           formattedErrors[path[0]] = message;
//         });
//         setErrors(formattedErrors);
//       } else {
//         toast.error("An unexpected error occurred. Please try again.");
//         console.error("Registration error:", err);
//       }
//     }
//   };

//   const handleGoogleSuccess = async (credentialResponse) => {
//     try {
//       setGoogleLoading(true);

//       const response = await axios.post(
//         `${AQ_BASE_URL}/aq/api/auth/google/callback`,
//         {
//           id_token: credentialResponse.credential,
//         }
//       );

//       const data = response.data;

//       // Store tokens in cookies
//       Cookies.set("access_token", data.access_token, {
//         expires: 1, // expires in 1 day
//         secure: true, // only works on HTTPS
//         sameSite: "Strict", // prevents CSRF
//         path: "/",
//       });

//       Cookies.set("refresh_token", data.refresh_token, {
//         expires: 7, // expires in 7 days
//         secure: true,
//         sameSite: "Strict",
//         path: "/",
//       });

//       // Redirect to the dashboard
//       window.location.href = data.redirect_url;
//     } catch (error) {
//       console.error(
//         "Google OAuth failed:",
//         error?.response?.data || error.message
//       );
//       alert("Google login failed. Please try again.");
//     } finally {
//       setGoogleLoading(false);
//     }
//   };

//   const handleGoogleError = () => {
//     console.error("Google OAuth failed");
//     alert("Google login failed. Please try again.");
//   };

//   return (
//     <div className="flex items-center justify-center min-h-screen bg-gray-100 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
//       <div className="w-full max-w-md bg-white border border-gray-300 rounded-lg shadow-lg p-6 sm:p-8 mx-2 sm:mx-0">
//         <h1 className="mt-1 text-2xl font-bold text-black text-center">
//           Register
//         </h1>
//         <h1 className="text-sm text-gray-500 text-center">
//           Enter your credentials to register your account.
//         </h1>
//         <form onSubmit={handleSubmit} className="space-y-2 mt-6">
//           <div className="flex flex-col sm:flex-row sm:gap-3 w-full">
//             <InputField
//               id="first_name"
//               name="first_name"
//               label="First Name"
//               type="text"
//               placeholder="Enter your first name."
//               value={formData.first_name}
//               onChange={handleChange}
//               error={errors.first_name}
//               className="flex-1"
//             />
//             <InputField
//               id="last_name"
//               name="last_name"
//               label="Last Name"
//               type="text"
//               placeholder="Enter your last name."
//               value={formData.last_name}
//               onChange={handleChange}
//               error={errors.last_name}
//               className="flex-1"
//             />
//           </div>

//           <div className="flex flex-col sm:flex-row sm:gap-3 w-full justify-between">
//             <InputField
//               id="username"
//               name="username"
//               label="Username"
//               type="text"
//               placeholder="Enter your username."
//               value={formData.username}
//               onChange={handleChange}
//               error={errors.username}
//               className="flex-1 "
//             />
//             <InputField
//               id="email"
//               name="email"
//               label="Email"
//               type="email"
//               placeholder="Enter your email."
//               value={formData.email}
//               onChange={handleChange}
//               error={errors.email}
//               className="flex-1"
//             />
//           </div>
//           <InputField
//             id="password"
//             name="password"
//             type={showPassword ? "text" : "password"}
//             label="Password"
//             placeholder="Enter your Password."
//             value={formData.password}
//             onChange={handleChange}
//             error={errors.password}
//             showPasswordToggle
//             onToggleShowPassword={() => setShowPassword(!showPassword)}
//           />
//           <InputField
//             id="confirmpassword"
//             name="confirmpassword"
//             type={showConfirmPassword ? "text" : "password"}
//             label="Confirm Password"
//             placeholder="Confirm your password."
//             value={formData.confirmpassword}
//             onChange={handleChange}
//             error={errors.confirmpassword}
//             showPasswordToggle
//             onToggleShowPassword={() =>
//               setShowConfirmPassword(!showConfirmPassword)
//             }
//           />
//           <div className="flex flex-col w-full">
//             <label
//               htmlFor="phone"
//               className="block text-sm font-medium text-gray-950"
//             >
//               Mobile No.
//             </label>
//             <div className="flex items-center gap-1 mt-1">
//               <div className="border border-gray-300 rounded-md">
//                 <select
//                   id="countryCode"
//                   name="countryCode"
//                   value={formData.countryCode}
//                   onChange={handleChange}
//                   className="block w-20 border-none rounded-md py-2 px-2.5 text-gray-900 text-xs"
//                 >
//                   <option value="+91">+91 (India)</option>
//                   <option value="+1">+1 (US)</option>
//                 </select>
//               </div>
//               <div className="flex-1">
//                 <InputField
//                   id="phone"
//                   name="phone"
//                   type="text"
//                   placeholder="Enter your mobile number."
//                   value={formData.phone}
//                   onChange={handleChange}
//                   className="w-full border-gray-300  rounded-md px-2 py-1"
//                 />
//               </div>
//             </div>
//             {errors.phone && (
//               <span className="text-red-700 ml-20 text-xs">{errors.phone}</span>
//             )}
//           </div>

//           <div className="flex flex-col items-center justify-center mt-4">
//             <div className="flex items-center">
//               <input
//                 id="termsAccepted"
//                 name="termsAccepted"
//                 type="checkbox"
//                 checked={formData.termsAccepted}
//                 onChange={handleChange}
//                 className="h-4 w-4 text-green-600 border-gray-300 rounded"
//               />
//               <label
//                 htmlFor="termsAccepted"
//                 className="ml-2 text-sm text-gray-900"
//               >
//                 I accept the terms and conditions
//               </label>
//             </div>
//             {errors.termsAccepted && (
//               <span className="text-red-700 pl-1 text-xs">
//                 {errors.termsAccepted}
//               </span>
//             )}
//           </div>

//           <button
//             type="submit"
//             className="block w-full bg-black hover:bg-gray-800 text-white font-bold py-2 px-4 rounded-md"
//           >
//             Register
//           </button>

//           <div className="mt-4 text-center">
//             <span className="text-sm">
//               Already have an account?{" "}
//               <Link to="/login" className="text-gray-950 hover:underline">
//                 Login
//               </Link>
//             </span>
//           </div>
//         </form>
//       </div>
//       <ToastContainer position="top-center" autoClose={3000} hideProgressBar />
//     </div>
//   );
// };

// export default Register;
